import React, { Fragment, useState, useEffect, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { BulkImportBillingCharges, StoreBillingCharges } from "../../../api/masters/billingChargesAPI";
import { GetBillingChargesGroup } from "../../../api/masters/billingChargesGroupAPI";
import BulkImport from "../../../components/BulkImport";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// Validation schema
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  billingChargesGroupId: yup
    .string()
    .required("Billing Charges Group is required"),
  printSrNo: yup.string().optional(),
  type: yup.string().required("Type is required"),
  rateEditable: yup
    .string()
    .oneOf(["Yes", "No"])
    .required("Rate Editable is required"),
  rate: yup
    .number()
    .typeError("Rate must be a number")
    .required("Rate is required"),

  intlRate: yup.number().typeError("Intl Rate must be a number"),
  discountAllowed: yup.string().oneOf(["Yes", "No"]),
  nature: yup.string().required("Nature is required"),
  serviceTax: yup.string().oneOf(["Yes", "No"]),
  active: yup.string().oneOf(["Yes", "No"]),
  hsnCode: yup.string().optional(),
  roomRates: yup.array().of(
    yup.object().shape({
      roomCategory: yup.string().required("Room Category is required"),
      rates: yup
        .number()
        .typeError("Rates must be a number")
        .required("Rates is required")
    })
  ),
  packageDetails: yup.array().of(
    yup.object().shape({
      chargesName: yup.string().required("Charges Name is required"),
      rates: yup
        .number()
        .typeError("Rates must be a number")
        .required("Rates is required")
    })
  )
});

const AddBillingCharges = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [groups, setGroups] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      rate: 0,
      intlRate: 0,
      roomRates: [
        {
          roomCategory: "",
          rates: ""
        }
      ],
      packageDetails: [
      ]
    }
  });

  // ✅ Fixed Room Categories
  const fixedRoomCategories = [
    { roomCategory: "CCU WARD", rates: 0 },
    { roomCategory: "EMERGENCY WARD", rates: 0 },
    { roomCategory: "GENERAL WARD", rates: 0 },
    { roomCategory: "ICU WARD", rates: 0 },
    { roomCategory: "NICU", rates: 0 },
    { roomCategory: "OT (Operation Theatre)", rates: 0 },
    { roomCategory: "SEMI PRIVATE WARD", rates: 0 },
    { roomCategory: "SPECIAL WARD", rates: 0 },
    { roomCategory: "VENTILATOR", rates: 0 }
  ];

  // Initialize with fixed categories
  useEffect(
    () => {
      fixedRoomCategories.forEach((room, index) => {
        setValue(`roomRates.${index}.roomCategory`, room.roomCategory);
        setValue(`roomRates.${index}.rates`, room.rates);
      });
    },
    [setValue]
  );

  const {
    fields: packageFields,
    append: appendPackage,
    remove: removePackage
  } = useFieldArray({
    control,
    name: "packageDetails"
  });

  // Fetch BillingCharges Groups
  const fetchGroups = async () => {
    try {
      const response = await GetBillingChargesGroup();
      if (response.success) setGroups(response.data);
    } catch (error) {
      showToast("error", "Failed to load billing charges groups");
    }
  };
  useEffect(() => {
      const loadData = async () => {
        try {
          dispatch({ type: "LOADER_START" });
  
          await fetchGroups();
        } catch (error) {
          showToast("error", error);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      };
  
      loadData();
    }, []);

  const onSubmit = async data => {
    try {
      dispatch({
        type: "LOADER_START"
      });
      const response = await StoreBillingCharges(data);
      if (response.success) {
        showToast("success", response.message);
        navigate("/masters/billing-charges/list");
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({
        type: "LOADER_STOP"
      });
    }
  };

  const formRef = useRef(null);
    useKeyboardSubmit(handleSubmit, onSubmit);
    useEnterNavigation(formRef);

  const bulkColumns = [
    { key: "billingChargesGroup", label: "Billing Charges Group", sample: "Room Charges" },
    { key: "name", label: "Charges Name", sample: "Room Charges" },
    { key: "rates", label: "Rates", sample: "100" },
    { key: "description", label: "Description", sample: "Room Charges" },
    { key: "active", label: "Active", sample: "Yes" }
  ];

  const handleBulkImport = async payload => {
    const response = await BulkImportBillingCharges(payload);

    if (response.success) {
      showToast("success", response.message);
      navigate("/masters/billing-charges/list");
    } else {
      showToast("error", response.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumb parent="Billing Charges" title="Add Billing Charge" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Add Billing Charges</h5>
                <Link to={`/masters/billing-charges/list`}>
                  <button className="btn btn-primary" id="list">
                    List Billing Charges
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Billing Charges
                  </UncontrolledTooltip>
                </Link>
              </div>
              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    {/* Name */}
                    <div className="col-6">
                      <div className="form-group">
                        <label>Name *</label>
                        <input
                          className="form-control"
                          {...register("name")}
                          placeholder="Billing Charge Name"
                        />
                        {errors.name &&
                          <p className="text-danger">
                            {errors.name.message}
                          </p>}
                      </div>
                    </div>

                    {/* Billing Charges Group */}
                    <div className="col-6">
                      <div className="form-group">
                        <label>Billing Charges Group *</label>
                        <select
                          className="form-control"
                          {...register("billingChargesGroupId")}
                        >
                          <option value="">Select Group</option>
                          {groups.map(group =>
                            <option key={group._id} value={group._id}>
                              {group.name}
                            </option>
                          )}
                        </select>
                        {errors.billingChargesGroupId &&
                          <p className="text-danger">
                            {errors.billingChargesGroupId.message}
                          </p>}
                      </div>
                    </div>

                    {/* Type */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>Print Sr No</label>
                        <input
                          type="number"
                          className="form-control"
                          {...register("printSrNo")}
                          placeholder="Print Sr No"
                        />
                        {errors.printSrNo &&
                          <p className="text-danger">
                            {errors.printSrNo.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-4">
                      <div className="form-group">
                        <label>Type *</label>
                        <select
                          className="form-control"
                          {...register("type")}
                          placeholder="Type"
                        >
                          <option value="">Select Type</option>
                          <option value="Charges">Charges</option>
                          <option value="Room Rent">Room Rent</option>
                          <option value="Doctor Visit">Doctor Visit</option>
                          <option value="Operation">Operation</option>
                          <option value="Package">Package</option>
                        </select>
                        {errors.type &&
                          <p className="text-danger">
                            {errors.type.message}
                          </p>}
                      </div>
                    </div>

                    {/* Rate Editable */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>Rate Editable *</label>
                        <select
                          className="form-control"
                          {...register("rateEditable")}
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                        {errors.rateEditable &&
                          <p className="text-danger">
                            {errors.rateEditable.message}
                          </p>}
                      </div>
                    </div>

                    {/* Rate */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>Rate</label>
                        <input
                          className="form-control"
                          type="number"
                          {...register("rate")}
                          placeholder="Rate"
                        />
                        {errors.rate &&
                          <p className="text-danger">
                            {errors.rate.message}
                          </p>}
                      </div>
                    </div>

                    {/* Intl Rate */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>Intl Rate</label>
                        <input
                          className="form-control"
                          type="number"
                          {...register("intlRate")}
                          placeholder="Intl Rate"
                        />
                        {errors.intlRate &&
                          <p className="text-danger">
                            {errors.intlRate.message}
                          </p>}
                      </div>
                    </div>

                    {/* Discount Allowed */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>Discount Allowed *</label>
                        <select
                          className="form-control"
                          {...register("discountAllowed")}
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                        {errors.discountAllowed &&
                          <p className="text-danger">
                            {errors.discountAllowed.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-4">
                      <div className="form-group">
                        <label>Nature *</label>
                        <select
                          className="form-control"
                          {...register("nature")}
                          placeholder="Nature"
                        >
                          <option value="">Select Nature</option>
                          <option value="Quantity">Quantity</option>
                          <option value="Daily">Daily</option>
                        </select>
                        {errors.nature &&
                          <p className="text-danger">
                            {errors.nature.message}
                          </p>}
                      </div>
                    </div>

                    {/* Service Tax */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>Service Tax *</label>
                        <select
                          className="form-control"
                          {...register("serviceTax")}
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                        {errors.serviceTax &&
                          <p className="text-danger">
                            {errors.serviceTax.message}
                          </p>}
                      </div>
                    </div>

                    {/* Active */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>Active</label>
                        <select
                          className="form-control"
                          {...register("active")}
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                    </div>

                    {/* HSN Code */}
                    <div className="col-4">
                      <div className="form-group">
                        <label>HSN Code</label>
                        <input
                          className="form-control"
                          {...register("hsnCode")}
                          placeholder="HSN Code"
                        />
                        {errors.hsnCode &&
                          <p className="text-danger">
                            {errors.hsnCode.message}
                          </p>}
                      </div>
                    </div>
                    </div>
                    <div className="row">

                    {/* Room Rates */}
                    <div className="col-6 mt-3">
                      <label>Room Rates</label>
                      {fixedRoomCategories.map((room, index) => (
                        <div className="d-flex mb-2" key={room.roomCategory}>
                          <input
                            className="form-control me-2"
                            {...register(`roomRates.${index}.roomCategory`)}
                            value={room.roomCategory}
                            readOnly
                          />
                          <input
                            className="form-control me-2"
                            type="number"
                            {...register(`roomRates.${index}.rates`)}
                            placeholder="Rate"
                          />
                          {errors.roomRates?.[index]?.rates && (
                            <p className="text-danger">
                              {errors.roomRates[index].rates.message}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Package Details */}
                    <div className="col-6 mt-3">
                      <label>Package Details</label>
                      {packageFields.map((field, index) =>
                        <div className="d-flex mb-2" key={field.id}>
                          <input
                            className="form-control me-2"
                            {...register(`packageDetails.${index}.chargesName`)}
                            placeholder="Charge Name"
                          />
                          <input
                            className="form-control me-2"
                            type="number"
                            {...register(`packageDetails.${index}.rates`)}
                            placeholder="Rate"
                          />
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => removePackage(index)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() =>
                          appendPackage({
                            chargesName: "",
                            rates: ""
                          })}
                      >
                        Add Package Detail
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>

            <BulkImport
              columns={bulkColumns}
              onSubmit={handleBulkImport}
              payloadKey="billingCharges"
              sampleFileName="billingCharge_sample.csv"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddBillingCharges;
